import { useApi } from "src/hooks/useApi";
import { usePollWhile } from "src/hooks/usePollWhile";
import { notifyRecipientDataStale } from "src/services/recipientDataRefresh";
import { useTranslation } from "react-i18next";
import { IoChevronForward } from "react-icons/io5";
import { TbClipboardCheck, TbClipboardList, TbFileTypePdf, TbSend, TbEye, TbHistory, TbBan } from "react-icons/tb";

interface PatientSubmission {
    firstName?: string;
    lastName?: string;
    birthDate?: string;
    email?: string;
    gender?: string;
}

interface QuestionnaireSubmission {
    // Only present for a real QuestionnaireSubmission document (digital self-completion).
    // A manually-confirmed consent (Recipient.consentDigitalSignedAt/consentPostSignedAt) is
    // synthesized by the backend as a timestamp-only entry with no id — never a real
    // submission to fetch a questionnaire PDF for, regardless of its "digital" source.
    id?: string;
    submittedAt: string;
    source?: "digital" | "post";
    salutation?: string;
    title?: string;
    firstName?: string;
    lastName?: string;
    birthDate?: string;
    email?: string;
    phones?: string[];
    street?: string;
    streetNumber?: string;
    plz?: string;
    city?: string;
    patients?: PatientSubmission[];
}

interface QuestionnaireEvent {
    type: "sent" | "resent" | "opened" | "submitted" | "revoked";
    date: string;
    source?: "digital" | "post";
}

interface Props {
    recipientId: string;
    onViewQuestionnairePdf?: () => void;
    onViewConsentPdf?: (channel: "digital" | "post") => void;
    awaitingSubmission?: boolean;
    awaitingSubmissionRecord?: boolean;
}

const EVENT_ICONS: Record<QuestionnaireEvent["type"], typeof TbSend> = {
    sent: TbSend,
    resent: TbSend,
    opened: TbEye,
    submitted: TbClipboardCheck,
    revoked: TbBan,
};

function formatDate(iso: string | undefined): string {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleString(undefined, {
        day: "2-digit", month: "2-digit", year: "numeric",
        hour: "2-digit", minute: "2-digit",
    });
}

export function RecipientQuestionnaireTab({
    recipientId,
    onViewQuestionnairePdf,
    onViewConsentPdf,
    awaitingSubmission = false,
    awaitingSubmissionRecord = false,
}: Props) {
    const { t } = useTranslation();
    const { data, loading, refetch } = useApi<{
        submission: QuestionnaireSubmission | null;
        submissions?: QuestionnaireSubmission[];
        questionnaireUrl?: string | null;
        events?: QuestionnaireEvent[];
    }>(`/recipients/${recipientId}/questionnaire`);

    const latest = data?.submission ?? null;
    const older = (data?.submissions ?? []).slice(1);
    const events = data?.events ?? [];

    const shouldPoll =
        (awaitingSubmission && !latest) ||
        (awaitingSubmissionRecord && !latest);
    const pollQuestionnaire = () => {
        notifyRecipientDataStale(recipientId);
        return refetch();
    };
    usePollWhile(shouldPoll, pollQuestionnaire, 5_000);

    if (loading) {
        return (
            <div className="q-tab">
                <div className="q-section">
                    <div aria-hidden style={{ width: 200, height: 18, borderRadius: 8, background: "#e5e7eb" }} />
                    <div aria-hidden style={{ width: "55%", height: 14, marginTop: 14, borderRadius: 8, background: "#e5e7eb" }} />
                </div>
            </div>
        );
    }

    return (
        <div className="q-tab">
            <div className="q-section">
                <div className="q-section__title">
                    {latest ? (
                        <TbClipboardCheck className="q-section__icon q-section__icon--green" />
                    ) : (
                        <TbClipboardList className="q-section__icon" />
                    )}
                    {t("recipientPage.questionnaire_submission")}
                </div>
                {latest ? (
                    <button
                        type="button"
                        className="q-submission-card"
                        onClick={() => (latest.id ? onViewQuestionnairePdf?.() : onViewConsentPdf?.(latest.source ?? "digital"))}
                        title={t("recipientPage.questionnaire_viewPdf")}
                    >
                        <span className="q-submission-card__main">
                            <span className="q-submission-card__date">
                                {t("recipientPage.questionnaire_submitted")} — {formatDate(latest.submittedAt)}
                            </span>
                        </span>
                        <span className="q-submission-card__action">
                            <TbFileTypePdf aria-hidden />
                            <span>{t("recipientPage.questionnaire_viewPdf")}</span>
                            <IoChevronForward aria-hidden />
                        </span>
                    </button>
                ) : (
                    <p className="q-empty">{t("recipientPage.questionnaire_noSubmission")}</p>
                )}

                {older.length > 0 && (
                    <div className="q-previous-submissions">
                        <p className="q-previous-submissions__label">
                            {t("recipientPage.questionnaire_previousSubmissions")}
                        </p>
                        {older.map((s, i) => (
                            <div key={i} className="q-submission-card q-submission-card--old">
                                <span className="q-submission-card__main">
                                    <span className="q-submission-card__date">
                                        {formatDate(s.submittedAt)}
                                    </span>
                                </span>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {events.length > 0 && (
                <div className="q-section">
                    <div className="q-section__title">
                        <TbHistory className="q-section__icon" />
                        {t("recipientPage.questionnaire_history")}
                    </div>
                    <div className="q-log">
                        {events.map((e, i) => {
                            const Icon = EVENT_ICONS[e.type];
                            return (
                                <div key={i} className="q-log__row">
                                    <Icon className={`q-log__icon q-log__icon--${e.type}`} />
                                    <span className="q-log__label">
                                        {t(`recipientPage.questionnaire_event_${e.type}`)}
                                    </span>
                                    <span className="q-log__date">{formatDate(e.date)}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
}
